import React, { useState, useEffect } from "react";
import { Card, Descriptions, Tag, Button, Space, Badge } from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useGet, useDynamicApi } from "../../hooks/useApi";
import { API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

const ThemeComponentDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [themesList, setThemesList] = useState([]);

  // API hooks
  const { execute: getItem, loading: itemLoading } = useDynamicApi("GET");
  const { execute: fetchThemesList } = useGet(
    API_ENDPOINTS.THEMES_LIST_WITHOUT_PAGINATE,
  );

  // Constants
  const AGE_GROUP_OPTIONS = [
    { value: "6-9", label: "6-9 years" },
    { value: "10-14", label: "10-14 years" },
    { value: "15-18", label: "15-18 years" },
  ];

  const COMPONENT_OPTIONS = [
    { value: "WARMUP", label: "Warm-up" },
    { value: "SKILLS", label: "Skills" },
    { value: "GAMES", label: "Games" },
    { value: "ATTACK", label: "Attack" },
    { value: "DEFENSE", label: "Defense" },
  ];

  // Load themes list
  const loadThemesList = async () => {
    try {
      const response = await fetchThemesList();
      if (response?.status === true && response.data) {
        setThemesList(response.data);
      }
    } catch (error) {
      console.error("Error loading themes:", error);
    }
  };

  // Load item details
  const loadItemDetails = async () => {
    setLoading(true);
    try {
      const endpoint = `${API_ENDPOINTS.THEMES_MANAGER_UPDATE}/${id}`;
      const response = await getItem(endpoint);

      if (response?.status === true && response.data) {
        setItem(response.data);
      } else {
        Toast.error(t("themeComponentManager.failedFetchDetails"));
        navigate("/themecomponent");
      }
    } catch (error) {
      console.error("Error fetching details:", error);
      Toast.error(
        error.message || t("themeComponentManager.failedFetchDetails"),
      );
      navigate("/themecomponent");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadItemDetails();
      loadThemesList();
    }
  }, [id]);

  // Helper functions
  const getAgeGroupLabel = (value) => {
    const option = AGE_GROUP_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : value || "N/A";
  };

  const getComponentLabel = (value) => {
    const option = COMPONENT_OPTIONS.find((opt) => opt.value === value);
    return option ? option.label : value || "N/A";
  };

  const getThemeName = (themeId) => {
    if (!themeId) return "N/A";
    const theme = themesList.find((t) => t.id === themeId);
    return theme ? theme.name : "N/A";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading || itemLoading) return null;

  if (!item) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <h3>{t("themeComponentManager.noComponentsFound")}</h3>
          <Button type="primary" onClick={() => navigate("/themecomponent")}>
            {t("common.previous")} {t("common.list")}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="theme-component-details-page">
      {/* Header with back button and actions */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/themecomponent")}
          >
            {t("common.previous")} {t("common.list")}
          </Button>
          <h2 style={{ margin: 0 }}>{t("themeComponentManager.details")}</h2>
        </Space>
        {/* Edit button commented out like in UserDetails */}
        {/* <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/theme-component/edit/${item.id}`)}
        >
          Edit Mapping
        </Button> */}
      </div>

      {/* Main Content */}
      <Card>
        {/* Avatar and Title Section */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <div>
            <h1 style={{ margin: 0, marginBottom: "8px" }}>
              {getThemeName(item.theme)}
            </h1>
            <Space size="middle">
              {/* <Tag color={item.is_active ? "green" : "default"}>
                {item.is_active ? t("common.active") : t("common.inactive")}
              </Tag> */}
              <Badge
                status={item.is_active ? "success" : "default"}
                text={
                  item.is_active ? t("common.active") : t("common.inactive")
                }
              />
            </Space>
          </div>
        </div>

        {/* Details in Descriptions */}
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t("themeManager.name")} span={2}>
            <Space>{getThemeName(item.theme)}</Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("themeComponentManager.ageGroup")}>
            {getAgeGroupLabel(item.age_group)}
          </Descriptions.Item>

          <Descriptions.Item
            label={t("themeComponentManager.component")}
            span={1}
          >
            {getComponentLabel(item.component)}
          </Descriptions.Item>

          <Descriptions.Item label={t("common.status")} span={1}>
            <Tag color={item.is_active ? "green" : "default"}>
              {item.is_active ? t("common.active") : t("common.inactive")}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label={t("common.createdAt")} span={1}>
            <Space>
              <CalendarOutlined />
              {formatDate(item.created_at)}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("common.lastUpdated")} span={1}>
            <Space>
              <ClockCircleOutlined />
              {formatDate(item.updated_at)}
            </Space>
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};

export default ThemeComponentDetails;
